import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { ACCOUNTS } from '../../api/accounts';
import { postJournal } from '../../api/journal';

const OVERHEAD_ACCOUNTS = [
  { id: 'f6027582-55d5-44b1-8cc2-e541ea2d9842', code: '6000', name: 'Staff Salaries' },
  { id: 'ff0f2b69-9e4f-4517-a2f6-d57612200877', code: '6010', name: 'Office Rent' },
  { id: '62570a06-75b8-464c-a399-025a5c474eda', code: '6020', name: 'Utilities' },
  { id: 'ac169ba5-732b-49b0-be13-13de12d9fba0', code: '6030', name: 'Internet & Communications' },
  { id: '4d432dd7-9722-457d-a9f4-5c9318a817e7', code: '6040', name: 'Marketing & Advertising' },
  { id: '3153a670-a75e-46b2-9445-82592752a8ae', code: '6050', name: 'Bank Charges' },
  { id: '32f0879f-ac93-4d59-a566-54f13e2e9492', code: '6060', name: 'Credit Card Fees' },
  { id: '25aa0c2a-820c-4bfe-ba88-5c977a3be214', code: '6070', name: 'Professional Fees' },
  { id: '35901629-48af-45ba-a991-a99cdcd82766', code: '6080', name: 'Software & Subscriptions' },
  { id: 'c3349a35-0ccc-4ceb-a835-7413a7b0aeb5', code: '6090', name: 'Travel & Entertainment' },
  { id: 'ef0c7e43-82f8-4edf-a04c-8d3138af33c7', code: '6100', name: 'Office Supplies' },
  { id: '35a2ff1a-f91d-4861-8bf4-d4aa6a211e61', code: '6110', name: 'Depreciation' },
  { id: '4f17a4ee-2a5d-448d-b643-584c93ad4ce4', code: '6130', name: 'Miscellaneous Expenses' },
];

export default function BillForm() {
  const navigate = useNavigate();
  const { user, branchId, role } = useAuth();
  const isSadmin = role === 'sadmin';
  const username = user?.email?.split('@')[0] || 'unknown';

  const [form, setForm] = useState({
    payee: '',
    expense_account_id: '',
    amount: '',
    bill_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    reference: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
  }, []);
  useEffect(() => { if (branchId && !selectedBranchId) setSelectedBranchId(branchId); }, [branchId]);

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function save() {
    if (!form.payee.trim()) { setError('Payee is required.'); return; }
    if (!form.expense_account_id) { setError('Select an expense account.'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setError('Enter a valid amount.'); return; }
    if (!form.bill_date) { setError('Bill date is required.'); return; }

    setSaving(true);
    setError('');

    const billNumber = 'BILL-' + new Date().getFullYear() + '-' + Date.now().toString().slice(-5);
    const amount = parseFloat(form.amount);

    // Post journal: DR Expense / CR Other Payables
    const je = await postJournal({
      date: form.bill_date,
      reference_type: 'BILL',
      description: `Bill — ${form.payee}${form.description ? ': ' + form.description : ''}`,
      lines: [
        { account_id: form.expense_account_id, debit_amount: amount, credit_amount: 0 },
        { account_id: ACCOUNTS.OTHER_PAYABLES,  debit_amount: 0,      credit_amount: amount },
      ],
      created_by: username,
      branch_id: selectedBranchId || null,
    });

    if (!je?.id) { setError('Failed to post journal entry.'); setSaving(false); return; }

    const { data, error: err } = await supabase.from('bills').insert({
      bill_number: billNumber,
      payee: form.payee.trim(),
      expense_account_id: form.expense_account_id,
      amount,
      base_amount: amount,
      bill_date: form.bill_date,
      due_date: form.due_date || null,
      reference: form.reference.trim() || null,
      description: form.description.trim() || null,
      status: 'UNPAID',
      journal_entry_id: je.id,
      created_by: username,
      branch_id: selectedBranchId || null,
    }).select('id').single();

    setSaving(false);
    if (err) { setError(err.message); return; }
    await supabase.from('journal_entries').update({ reference_id: data.id }).eq('id', je.id);
    navigate(`/bills/${data.id}`);
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <div className="page-header">
        <div>
          <div className="page-title">New Bill</div>
          <div className="page-subtitle">Record a payable — expense is posted immediately</div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" style={{ minWidth: 140 }} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate('/bills')}>← Back</button>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Payee <span style={{ color: '#dc2626' }}>*</span></label>
            <input className="form-input" placeholder="e.g. Landlord, CEB, SLT, Water Board…"
              value={form.payee} onChange={e => set('payee', e.target.value)} />
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Expense Account <span style={{ color: '#dc2626' }}>*</span></label>
            <select className="form-input" value={form.expense_account_id} onChange={e => set('expense_account_id', e.target.value)}>
              <option value="">— Select account —</option>
              {OVERHEAD_ACCOUNTS.map(a => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Amount (LKR) <span style={{ color: '#dc2626' }}>*</span></label>
            <input className="form-input" type="number" min="0" step="0.01" placeholder="0.00"
              value={form.amount} onChange={e => set('amount', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Supplier Reference</label>
            <input className="form-input" placeholder="Invoice / bill number from supplier"
              value={form.reference} onChange={e => set('reference', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Bill Date <span style={{ color: '#dc2626' }}>*</span></label>
            <input className="form-input" type="date"
              value={form.bill_date} onChange={e => set('bill_date', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Due Date</label>
            <input className="form-input" type="date"
              value={form.due_date} onChange={e => set('due_date', e.target.value)} />
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Description / Notes</label>
            <input className="form-input" placeholder="Optional note (e.g. June 2026 rent)"
              value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
        </div>

        {/* Journal preview */}
        {isSadmin && form.expense_account_id && parseFloat(form.amount) > 0 && (
          <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginTop: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>Journal Preview</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '4px 16px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Account</span>
              <span style={{ color: 'var(--text-muted)', textAlign: 'right' }}>DR</span>
              <span style={{ color: 'var(--text-muted)', textAlign: 'right' }}>CR</span>
              <span>{OVERHEAD_ACCOUNTS.find(a => a.id === form.expense_account_id)?.name}</span>
              <span style={{ textAlign: 'right', fontWeight: 600 }}>{parseFloat(form.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              <span style={{ textAlign: 'right' }}>—</span>
              <span>Other Payables (2060)</span>
              <span style={{ textAlign: 'right' }}>—</span>
              <span style={{ textAlign: 'right', fontWeight: 600 }}>{parseFloat(form.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        )}

        {error && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{error}</div>}

        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Bill'}
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/bills')}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
